import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';

import { parseJsonString } from '../helpers/string-utils';
import { SERVER_REQUEST, SERVER_RESPONSE, ServerRequest, ServerResponse } from '../server/server.tokens';

function parseCookies(cookie: string): Record<string, unknown> {
  return decodeURIComponent(cookie || '')
    .split(';')
    .reduce((acc, curr) => {
      if (curr) {
        const parts = curr.split('=');
        return {
          ...acc,
          [parts[0].trim()]: parseJsonString((parts[1] || '').trim())
        };
      } else {
        return acc;
      }
    }, {});
}

function stringifyValue(value: unknown): unknown {
  return typeof value === 'object' ? JSON.stringify(value) : value;
}

function stringifyOptions(options: Record<string, unknown>): string {
  return Object.keys(options).reduce((acc, curr) => `${acc}${curr}=${options[curr]};`, '');
}

@Injectable({
  providedIn: 'root'
})
export class CookieService {
  constructor(
    @Optional() @Inject(SERVER_REQUEST) private readonly request: ServerRequest,
    @Optional() @Inject(SERVER_RESPONSE) private readonly response: ServerResponse,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  getAll(): Record<string, unknown> {
    return parseCookies(this.getCookieHeader());
  }

  get<T = unknown>(name: string): T {
    return this.getAll()[name] as T;
  }

  set(name: string, value: unknown, options: Record<string, unknown> = { path: '/' }): void {
    if (this.response) {
      this.response.cookie(name, value, options);
    } else {
      this.document.cookie = `${name}=${stringifyValue(value)}; ${stringifyOptions(options)}`;
    }
  }

  delete(name: string, path = '/'): void {
    this.response ? this.response.clearCookie(name) : this.set(name, '', { 'Max-Age': 0, path });
  }

  private getCookieHeader(): string {
    return this.request ? this.request.headers.cookie : this.document.cookie;
  }
}
